import tw from 'twin.macro';

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { FaceFrownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import Accordion from '@/components/Accordion';
import AccordionSkeleton from '@/components/AccordionSkeleton';
import QuestionDetails from '@/components/BackofficeQuestionDetails';
import QuestionHighlight from '@/components/card/QuestionHighlight';
import { IntervieweeVideoContainer } from '@/containers';
import TranscribeContainer from '@/containers/GenerativeAI/TranscribeContainer';

import { QuestionSnapshotContainerProps } from './types';

const QuestionSnapshotContainer = ({
  position,
  questionSnapshotLoading,
  questionSnapshot,
}: QuestionSnapshotContainerProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <h5 tw="mb-2">วิดีโอการสัมภาษณ์</h5>
      {questionSnapshotLoading ? (
        <AccordionSkeleton lines={8} />
      ) : (
        <>
          {Array.isArray(questionSnapshot) && questionSnapshot.length === 0 ? (
            <div tw="flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border bg-white p-14">
              <FaceFrownIcon tw="h-6 w-6" />
              <h5>ไม่พบการสัมภาษณ์</h5>
            </div>
          ) : (
            questionSnapshot.map((snapshot, index) => {
              return (
                <Accordion
                  index={index}
                  key={`Accordion.${index}`}
                  summary={
                    <div tw="relative w-[90%] sm:w-[80%]">
                      <div tw="flex items-center gap-2">
                        <QuestionDetails number={index + 1} title={snapshot.title} />
                        {snapshot.videoVerified === false && (
                          <span
                            css={[tw`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700`]}
                            title={snapshot.videoProcessingError ?? t('sessionViewer.video.corruptedFallback')}
                          >
                            <ExclamationTriangleIcon tw="h-3.5 w-3.5" />
                            {t('sessionViewer.video.corruptedBadge')}
                          </span>
                        )}
                      </div>
                      {snapshot.highlightCount != null && snapshot.highlightCount >= 0 && (
                        <QuestionHighlight highlightCount={snapshot.highlightCount} />
                      )}
                    </div>
                  }
                  detail={
                    <div tw="grid gap-4 w-full lg:grid-cols-2 grid-cols-1">
                      <div>
                        <IntervieweeVideoContainer
                          controls
                          src={snapshot.videoUrl ?? snapshot.url ?? undefined}
                          fullWidth
                        />
                        {snapshot.videoVerified === false && snapshot.videoProcessingError && (
                          <p tw="mt-2 text-xs text-red-600">
                            {snapshot.videoProcessingError}
                          </p>
                        )}
                      </div>
                      <div tw="grid gap-2">
                        <TranscribeContainer
                          transcribedText={snapshot.transcribedText ?? ''}
                          jobRole={position}
                          question={snapshot.title}
                          transcribedData={snapshot.processedTranscribedText}
                        />
                      </div>
                    </div>
                  }
                />
              );
            })
          )}
        </>
      )}
    </div>
  );
};

export default QuestionSnapshotContainer;
